import {assertAndContinue} from 'Asserts';
import {
  BulkAssemblyValidationMessages,
  ValidationStatus,
} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {assertNever, LOADING} from 'util/Types';

/**
 * Get the underlying string value for a "raw text" table cell.
 */
export function getCellText(cell: Cell): string {
  if (cell.typedValue != null) {
    return formatTypedValue(cell.typedValue);
  } else {
    return cell.text;
  }
}

export const BULK_ASSEMBLY_ERROR_PREFIX = 'Error: ';
export const BULK_ASSEMBLY_WARNING_PREFIX = 'Warning: ';
export const BULK_ASSEMBLY_SUCCESS_PREFIX = 'Success: ';
export const BULK_ASSEMBLY_EMPTY_SUCCESS_MESSAGE = 'Success';
export const BULK_ASSEMBLY_VALIDATING_MESSAGE = 'Validating...';
export const BULK_ASSEMBLY_LOADING_MESSAGE = 'Loading...';

/**
 * Convert an array of validation messages into a text format that can fit into a table cell.
 *
 * Ideally we would pass the full validation information to the table cell, but it currently doesn't fit
 * into a TypedValue. Instead, we format the string in a particular way and parse it back at render time.
 */
export function formatValidationMessages(
  validationMessages: BulkAssemblyValidationMessages,
  isReadOnly: boolean
): string {
  if (validationMessages === LOADING) {
    if (!isReadOnly) {
      return BULK_ASSEMBLY_VALIDATING_MESSAGE;
    } else {
      return BULK_ASSEMBLY_LOADING_MESSAGE;
    }
  }
  return validationMessages
    .map((message) => {
      switch (message.status) {
        case ValidationStatus.SUCCESS:
          if (message.message) {
            return BULK_ASSEMBLY_SUCCESS_PREFIX + message.message;
          } else {
            // Finalized assemblies have success cells with no message. Use just the string "Success"
            // as the cell text rather than "Success: " so that copy and CSV export look nicer.
            return BULK_ASSEMBLY_EMPTY_SUCCESS_MESSAGE;
          }
        case ValidationStatus.WARNING:
          return BULK_ASSEMBLY_WARNING_PREFIX + message.message;
        case ValidationStatus.ERROR:
          return BULK_ASSEMBLY_ERROR_PREFIX + message.message;
        default:
          assertAndContinue(
            false,
            `Unexpected bulk assembly validation status: ${assertNever(message.status)}`
          );
          return message.message;
      }
    })
    .join('\n');
}

export function maybeMakeCellError(errorMessage?: string): EvaluatedCellError | undefined {
  if (errorMessage != null) {
    return {type: 'Value error', message: errorMessage};
  } else {
    return undefined;
  }
}
